import { api } from "@/api/api";
import type { ApiResponse, Program } from "@/types/api/types";

type MessageRow = {
  uuid: string;
  id: string;
  type: 'MESSAGE';
  programId: string;
  messageContent: string;
  messageActive: boolean;
  messageSeverity: any;
  originalProgram: Program;
}

type ExtendedRow = Program | MessageRow;

export const ProgramsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPrograms: builder.query<ApiResponse<Program>, void>({
      query: () => ({
        url: "programs",
      }),
      providesTags: ["Programs"],
    }),
    getProgramsWithPolling: builder.query<ApiResponse<Program>, void>({
      query: () => ({
        url: "programs",
      }),
      providesTags: ["Programs"],
    }),
    getProgram: builder.query<Program, string>({
      query: (uuid) => `programs/${uuid}`,
      providesTags: (result, error, uuid) => [{ type: "Programs" as const, id: uuid }],
    }),
    updateProgram: builder.mutation<Program, { uuid: string; data: FormData }>({
      query: ({ uuid, data }) => ({
        url: `programs/${uuid}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Programs"],
    }),
    // Nouveau endpoint pour visualiser le guide
    getProgramGuide: builder.query<Blob, string>({
      query: (uuid) => ({
        url: `programs/${uuid}/guide`,
        responseHandler: (response) => response.blob(),
      }),
      providesTags: (result, error, uuid) => [{ type: "Programs" as const, id: `${uuid}-guide` }],
    }),
    // Endpoint mis à jour pour l'upload (POST vers /programs/{uuid}/guide)
    uploadProgramGuide: builder.mutation<
      { success: boolean; message: string; guideFilePath?: string },
      { uuid: string; file: File }
    >({
      query: ({ uuid, file }) => {
        const formData = new FormData()
        formData.append('guide', file) // Nom attendu par ProgramWrite->getGuide()

        return {
          url: `programs/${uuid}/guide`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['Programs'],
    }),
    removeProgramGuide: builder.mutation<
      void,
      string
    >({
      query: (uuid) => ({
        url: `programs/${uuid}/guide`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Programs'],
    }),
    updateProgramMessage: builder.mutation<
      Program,
      {
        uuid: string;
        data: {
          messageContent: string;
          messageSeverity: string;
          messageActive: boolean;
        }
      }
    >({
      query: ({ uuid, data }) => ({
        url: `programs/${uuid}/message`,
        method: 'PUT',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['Programs'],
    }),
    deleteProgramMessage: builder.mutation<void, string>({
      query: (uuid) => ({
        url: `programs/${uuid}/message`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Programs'],
    }),
    getProgramsWithMessages: builder.query<ApiResponse<ExtendedRow>, void>({
      query: () => ({
        url: "programs",
      }),
      transformResponse: (response: ApiResponse<Program>) => {
        if (!response?.member) {
          return response;
        }

        const transformProgramsToExtendedRows = (programs: Program[]): ExtendedRow[] => {
          const rows: ExtendedRow[] = [];

          programs.forEach((program: Program) => {
            // Ajouter la ligne du programme
            rows.push(program);

            // Si le programme a un message, ajouter une ligne de message
            if (program.messageContent?.trim()) {
              rows.push({
                uuid: `${program.uuid}-message`,
                id: `${program.uuid}-message`,
                type: 'MESSAGE',
                programId: program.uuid,
                messageContent: program.messageContent,
                messageActive: program.messageActive,
                messageSeverity: program.messageSeverity,
                originalProgram: program,
              } as MessageRow);
            }
          });

          return rows;
        };

        return {
          ...response,
          member: transformProgramsToExtendedRows(response.member)
        };
      },
      providesTags: ["Programs"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProgramsQuery,
  useGetProgramsWithMessagesQuery,
  useGetProgramsWithPollingQuery,
  useGetProgramQuery,
  useUpdateProgramMutation,
  useGetProgramGuideQuery,
  useUploadProgramGuideMutation,
  useRemoveProgramGuideMutation,
  useUpdateProgramMessageMutation,
  useDeleteProgramMessageMutation
} = ProgramsApi;
